import { useCallback, useEffect, useRef, useState, startTransition } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { getWorkspaceById } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { mapWorkspaceOverviewBody } from '../../utils';

/**
 * GET /v1/workspace/:id for detail layout + overview tab (refetch via listRefreshKey from list context).
 */
export function useWorkspaceDetailQuery({ workspaceId, listRefreshKey }) {
  const { t } = useTranslation('workspaces');
  const [workspaceDetail, setWorkspaceDetail] = useState(null);
  const [workspaceDetailLoading, setWorkspaceDetailLoading] = useState(true);
  const [workspaceDetailError, setWorkspaceDetailError] = useState(null);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const prevWorkspaceIdForDetailRef = useRef(undefined);

  const refreshWorkspaceDetail = useCallback(() => {
    setDetailRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!workspaceId) return undefined;

    const workspaceChanged = prevWorkspaceIdForDetailRef.current !== workspaceId;
    prevWorkspaceIdForDetailRef.current = workspaceId;

    let cancelled = false;
    startTransition(() => {
      setWorkspaceDetailError(null);
      if (workspaceChanged) {
        setWorkspaceDetail(null);
        setWorkspaceDetailLoading(true);
      }
    });

    getWorkspaceById(workspaceId)
      .then((res) => {
        if (cancelled) return;
        const normalized = validateApiResponse(res, t('detail.overview.loadError'));
        const mapped = mapWorkspaceOverviewBody(normalized.body);
        setWorkspaceDetail(mapped);
        if (!mapped) setWorkspaceDetailError(t('detail.overview.loadError'));
        else setWorkspaceDetailError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (workspaceChanged) setWorkspaceDetail(null);
        setWorkspaceDetailError(err?.message ?? t('common.requestFailed'));
      })
      .finally(() => {
        if (!cancelled) setWorkspaceDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, listRefreshKey, detailRefreshKey, t]);

  return {
    workspaceDetail,
    workspaceDetailLoading,
    workspaceDetailError,
    refreshWorkspaceDetail,
  };
}
